import type { AxiosError, AxiosResponse } from "axios";
import type {
  AskHelenaConversation,
  AskHelenaConversationMessage,
  AskHelenaConversationsPage,
  AskHelenaFeedbackRating,
  AskHelenaStopResult,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

/**
 * Conversation history for Ask Helena (helena-ask-service). Unlike `/api/v1/ask`
 * next door these are ordinary JSON requests, so they go back to the api-layer
 * convention: `axiosClient` with an absolute URL (which overrides its baseURL),
 * so the request interceptor still attaches a fresh Logto token for
 * VITE_APP_BASE_URL — the audience the ask service accepts.
 *
 * Both endpoints are scoped to the calling technician server-side; a colleague's
 * thread is a 404, indistinguishable from an id that never existed.
 */

const ASK_BASE_URL = import.meta.env.VITE_APP_HELENA_ASK_BASE_URL;

export const getAskHelenaConversations = async ({
  page,
  pageSize,
  search,
}: {
  page: number;
  pageSize: number;
  /**
   * Case-insensitive substring match over the title AND the message text, so a
   * thread is found by a word the user remembers typing. `%` and `_` match
   * literally — the server escapes them.
   */
  search?: string;
}): Promise<AskHelenaConversationsPage> => {
  try {
    const response = await axiosClient.get(
      `${ASK_BASE_URL}/api/v1/conversations`,
      // A blank term is treated as absent server-side; omitting it keeps the
      // request URL honest about what was actually asked for.
      { params: { page, pageSize, ...(search ? { search } : {}) } },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Pinning is idempotent — re-pinning an already-pinned thread is a 200 and is
 * exempt from the 20-thread cap, so a client that pins defensively cannot wedge
 * itself. The 21st distinct pin is a 400 whose message is written to be read by
 * a user; show it verbatim.
 */
export const pinAskHelenaConversation = async ({
  conversationId,
}: {
  conversationId: string;
}): Promise<AskHelenaConversation> => {
  try {
    const response = await axiosClient.put(
      `${ASK_BASE_URL}/api/v1/conversations/${encodeURIComponent(conversationId)}/pin`,
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const unpinAskHelenaConversation = async ({
  conversationId,
}: {
  conversationId: string;
}): Promise<AskHelenaConversation> => {
  try {
    const response = await axiosClient.delete(
      `${ASK_BASE_URL}/api/v1/conversations/${encodeURIComponent(conversationId)}/pin`,
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Renames a thread. 1–200 characters — empty or whitespace-only is a 400, so
 * the caller validates first and this is only the backstop.
 *
 * A rename sets `titleSource` to "manual" and locks the auto-titler out
 * permanently, including a generation already in flight.
 */
export const renameAskHelenaConversation = async ({
  conversationId,
  title,
}: {
  conversationId: string;
  title: string;
}): Promise<AskHelenaConversation> => {
  try {
    const response = await axiosClient.patch(
      `${ASK_BASE_URL}/api/v1/conversations/${encodeURIComponent(conversationId)}`,
      { title: title.trim() },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Soft delete: the thread leaves the list and its messages endpoint, and can no
 * longer be resumed — which is everything a user means by "delete". The stored
 * messages and the spend audit trail are retained server-side, but there is no
 * undo endpoint and no trash to build.
 */
export const deleteAskHelenaConversation = async ({
  conversationId,
}: {
  conversationId: string;
}): Promise<void> => {
  try {
    const response = await axiosClient.delete(
      `${ASK_BASE_URL}/api/v1/conversations/${encodeURIComponent(conversationId)}`,
    );
    const resData = response.data;
    if (!resData.success) {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const stopAskHelenaConversation = async ({
  conversationId,
}: {
  conversationId: string;
}): Promise<AskHelenaStopResult> => {
  try {
    const response = await axiosClient.post(
      `${ASK_BASE_URL}/api/v1/conversations/${encodeURIComponent(conversationId)}/stop`,
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getAskHelenaConversationMessages = async ({
  conversationId,
}: {
  conversationId: string;
}): Promise<AskHelenaConversationMessage[]> => {
  try {
    const response = await axiosClient.get(
      `${ASK_BASE_URL}/api/v1/conversations/${encodeURIComponent(conversationId)}/messages`,
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data ?? [];
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const rateAskHelenaMessage = async ({
  messageId,
  rating,
}: {
  messageId: string;
  rating: AskHelenaFeedbackRating;
}): Promise<void> => {
  try {
    const response = await axiosClient.post(
      `${ASK_BASE_URL}/api/v1/messages/${encodeURIComponent(messageId)}/feedback`,
      { rating },
    );
    response.data;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
